import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { FiChevronLeft, FiChevronRight, FiSearch } from "react-icons/fi";
import { MoonLoader } from "react-spinners";
import Navbar from "../../components/player/Navbar.tsx";
import Footer from "../../components/player/Footer.jsx";
import AlertError from "../../components/alerts/AlertError.tsx";
import { usePlayerChrome } from "../../hooks/usePlayerChrome.ts";
import { canAccessVimplayGames, getPlayerVimplayBonuses, getVimplayGames, getVimplayLaunchUrl, VimplayPlayerBonus } from "../../services/player/VimplayService.tsx";
import { VimplayGame } from "../../models/VimplayGame.tsx";
import {
    compareVimplayLobbyGames,
    VIMPLAY_LOBBY_CATEGORIES,
    VimplayLobbyCategoryId,
    vimplayGameIsLive,
    vimplayLobbyCategory,
} from "../../models/vimplayCategories.ts";
import { useUser } from "../../context/UserContext.tsx";
import { logUserActivity } from "../../services/player/LoginService.tsx";
import "../../styles/Navigation.css";
import "../../styles/VimplayGames.css";

const TAP_MOVE_PX = 8;
const TAP_SCROLL_PX = 3;
const TAP_CONFIRM_MS = 70;

type CardGesture = {
    x: number;
    y: number;
    scrollY: number;
    scrollLeft: number;
    pointerId: number;
    pointerType: string;
    moved: boolean;
};

function GameCard({
    game,
    categoryLabel,
    isLive,
    bonus,
    isLaunching,
    isSelected,
    onLaunch,
}: {
    game: VimplayGame;
    categoryLabel: string;
    isLive: boolean;
    bonus?: VimplayPlayerBonus;
    isLaunching: boolean;
    isSelected: boolean;
    onLaunch: (game: VimplayGame, gameTab: Window | null) => void;
}) {
    const image = game.images?.sq || game.images?.ls;
    const webp = image?.webp || "";
    const org = image?.org || "";
    const [src, setSrc] = useState(webp || org);
    const gestureRef = useRef<CardGesture>({
        x: 0,
        y: 0,
        scrollY: 0,
        scrollLeft: 0,
        pointerId: -1,
        pointerType: "",
        moved: false,
    });
    const confirmTimerRef = useRef<number | null>(null);
    const pendingTabRef = useRef<Window | null>(null);

    useEffect(() => {
        setSrc(webp || org);
    }, [webp, org]);

    useEffect(() => {
        return () => {
            if (confirmTimerRef.current != null) window.clearTimeout(confirmTimerRef.current);
            pendingTabRef.current?.close();
            pendingTabRef.current = null;
        };
    }, []);

    const name = game.name?.trim() || "Game";

    const markMoved = (clientX: number, clientY: number) => {
        const gesture = gestureRef.current;
        if (Math.abs(clientX - gesture.x) > TAP_MOVE_PX || Math.abs(clientY - gesture.y) > TAP_MOVE_PX) {
            gesture.moved = true;
        }
    };

    const scrollShifted = (rail: HTMLElement | null) => {
        const gesture = gestureRef.current;
        const pageMoved = Math.abs(window.scrollY - gesture.scrollY) > TAP_SCROLL_PX;
        const railMoved = rail != null && Math.abs(rail.scrollLeft - gesture.scrollLeft) > TAP_SCROLL_PX;
        return gesture.moved || pageMoved || railMoved;
    };

    const openGameTab = () => window.open("about:blank", "_blank");

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (event.detail === 0) {
            onLaunch(game, openGameTab());
            return;
        }

        const rail = event.currentTarget.closest(".vimplay-rail");
        if (scrollShifted(rail instanceof HTMLElement ? rail : null)) {
            event.preventDefault();
            gestureRef.current.moved = false;
            return;
        }

        const pointerType = gestureRef.current.pointerType;
        if (pointerType !== "touch" && pointerType !== "pen") {
            onLaunch(game, openGameTab());
            return;
        }

        const gameTab = openGameTab();
        pendingTabRef.current = gameTab;
        const scrollY = window.scrollY;
        const scrollLeft = rail instanceof HTMLElement ? rail.scrollLeft : 0;
        if (confirmTimerRef.current != null) window.clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = window.setTimeout(() => {
            confirmTimerRef.current = null;
            const pageMoved = Math.abs(window.scrollY - scrollY) > TAP_SCROLL_PX;
            const railMoved = rail instanceof HTMLElement && Math.abs(rail.scrollLeft - scrollLeft) > TAP_SCROLL_PX;
            if (gestureRef.current.moved || pageMoved || railMoved) {
                gameTab?.close();
                if (pendingTabRef.current === gameTab) pendingTabRef.current = null;
                return;
            }
            if (pendingTabRef.current === gameTab) pendingTabRef.current = null;
            onLaunch(game, gameTab);
        }, TAP_CONFIRM_MS);
    };

    return (
        <button
            type="button"
            className={`vimplay-card${isLaunching ? " vimplay-card--launching" : ""}${isSelected ? " is-selected" : ""}`}
            disabled={isLaunching || game.id == null}
            aria-busy={isLaunching}
            onPointerDown={(event) => {
                if (event.pointerType === "mouse" && event.button !== 0) return;
                const rail = event.currentTarget.closest(".vimplay-rail");
                gestureRef.current = {
                    x: event.clientX,
                    y: event.clientY,
                    scrollY: window.scrollY,
                    scrollLeft: rail instanceof HTMLElement ? rail.scrollLeft : 0,
                    pointerId: event.pointerId,
                    pointerType: event.pointerType,
                    moved: false,
                };
            }}
            onPointerMove={(event) => {
                if (event.pointerId !== gestureRef.current.pointerId) return;
                markMoved(event.clientX, event.clientY);
            }}
            onPointerUp={(event) => {
                if (event.pointerId !== gestureRef.current.pointerId) return;
                markMoved(event.clientX, event.clientY);
            }}
            onPointerCancel={(event) => {
                if (event.pointerId !== gestureRef.current.pointerId) return;
                gestureRef.current.moved = true;
            }}
            onClick={handleClick}
        >
            <div className="vimplay-card-image-wrap">
                {src ? (
                    <img
                        className="vimplay-card-image"
                        src={src}
                        alt=""
                        draggable={false}
                        loading="lazy"
                        onError={() => {
                            if (src === webp && org && org !== webp) {
                                setSrc(org);
                                return;
                            }
                            setSrc("");
                        }}
                    />
                ) : (
                    <span className="vimplay-card-placeholder">{name}</span>
                )}
                {isLaunching && (
                    <span className="vimplay-card-spinner" role="status" aria-label="Opening game">
                        <MoonLoader color="#c6e03c" loading size={36} />
                    </span>
                )}
                {isLive && <span className="vimplay-card-live">Live</span>}
                {bonus && (
                    <span className="vimplay-card-bonus">
                        {bonus.freeBetRemaining} free · ${bonus.freeBetAmount}
                    </span>
                )}
            </div>
            <span className="vimplay-card-name">{name}</span>
            <span className="vimplay-card-category">{categoryLabel}</span>
        </button>
    );
}

function CategoryRail({
    title,
    games,
    bonuses,
    launchingGameId,
    selectedGameId,
    onLaunch,
}: {
    title: string;
    games: VimplayGame[];
    bonuses: VimplayPlayerBonus[];
    launchingGameId: number | null;
    selectedGameId: number | null;
    onLaunch: (game: VimplayGame, gameTab: Window | null) => void;
}) {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(1);
    const [canScrollBack, setCanScrollBack] = useState(false);
    const [canScrollForward, setCanScrollForward] = useState(false);

    const syncPages = useCallback(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const width = scroller.clientWidth;
        if (width <= 0) return;
        const maxScroll = scroller.scrollWidth - width;
        const count = Math.max(1, Math.ceil((scroller.scrollWidth - 1) / width));
        const current = Math.min(count - 1, Math.round(scroller.scrollLeft / width));
        setPageCount(count);
        setPage(current);
        setCanScrollBack(scroller.scrollLeft > 8);
        setCanScrollForward(scroller.scrollLeft < maxScroll - 8);
    }, []);

    useEffect(() => {
        syncPages();
        const scroller = scrollerRef.current;
        if (!scroller) return;
        scroller.addEventListener("scroll", syncPages, { passive: true });
        const observer = new ResizeObserver(syncPages);
        observer.observe(scroller);

        const onWheel = (event: WheelEvent) => {
            if (window.innerWidth < 1024) return;
            const maxScroll = scroller.scrollWidth - scroller.clientWidth;
            if (maxScroll <= 1) return;
            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
            if (delta === 0) return;
            const atStart = scroller.scrollLeft <= 0 && delta < 0;
            const atEnd = scroller.scrollLeft >= maxScroll - 1 && delta > 0;
            if (atStart || atEnd) return;
            event.preventDefault();
            scroller.scrollLeft += delta;
        };
        scroller.addEventListener("wheel", onWheel, { passive: false });

        return () => {
            scroller.removeEventListener("scroll", syncPages);
            scroller.removeEventListener("wheel", onWheel);
            observer.disconnect();
        };
    }, [syncPages, games.length]);

    const goToPage = (index: number) => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        scroller.scrollTo({ left: index * scroller.clientWidth, behavior: "smooth" });
    };

    const scrollRail = (direction: -1 | 1) => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const amount = Math.max(scroller.clientWidth * 0.85, 240);
        scroller.scrollBy({ left: direction * amount, behavior: "smooth" });
    };

    return (
        <section className="vimplay-section" aria-label={title}>
            <h2 className="vimplay-section-title">{title}</h2>
            <div className="vimplay-rail-wrap">
                {pageCount > 1 && (
                    <>
                        <button
                            type="button"
                            className="vimplay-arrow vimplay-arrow--prev"
                            aria-label={`Previous ${title} games`}
                            disabled={!canScrollBack}
                            onClick={() => scrollRail(-1)}
                        >
                            <FiChevronLeft aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            className="vimplay-arrow vimplay-arrow--next"
                            aria-label={`Next ${title} games`}
                            disabled={!canScrollForward}
                            onClick={() => scrollRail(1)}
                        >
                            <FiChevronRight aria-hidden="true" />
                        </button>
                    </>
                )}
                <div className="vimplay-rail" ref={scrollerRef}>
                    {games.map((game, index) => {
                        const category = vimplayLobbyCategory(game);
                        return (
                            <GameCard
                                key={`${game.id ?? game.name ?? "game"}-${index}`}
                                game={game}
                                categoryLabel={category.label}
                                isLive={vimplayGameIsLive(game)}
                                bonus={bonuses.find((bonus) => bonus.gameId === game.id)}
                                isLaunching={launchingGameId === game.id}
                                isSelected={selectedGameId === game.id}
                                onLaunch={onLaunch}
                            />
                        );
                    })}
                </div>
            </div>
            {pageCount > 1 && (
                <div className="vimplay-dots">
                    {Array.from({ length: pageCount }, (_, index) => (
                        <button
                            key={index}
                            type="button"
                            className={`vimplay-dot${index === page ? " is-active" : ""}`}
                            aria-label={`${title}, page ${index + 1}`}
                            onClick={() => goToPage(index)}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}

function LobbySkeleton() {
    return (
        <div className="vimplay-skeleton" aria-busy="true" aria-label="Loading games">
            <span className="vimplay-skeleton-search vimplay-sk" />
            <div className="vimplay-filters" aria-hidden="true">
                {["84px", "132px", "78px", "64px", "70px", "108px"].map((width) => (
                    <span key={width} className="vimplay-skeleton-chip vimplay-sk" style={{ width }} />
                ))}
            </div>
            <div className="vimplay-sections">
                {["148px", "96px", "118px"].map((titleWidth) => (
                    <section key={titleWidth} className="vimplay-section" aria-hidden="true">
                        <span className="vimplay-skeleton-title vimplay-sk" style={{ width: titleWidth }} />
                        <div className="vimplay-rail">
                            {Array.from({ length: 6 }, (_, index) => (
                                <div className="vimplay-card vimplay-skeleton-card" key={index}>
                                    <span className="vimplay-skeleton-image vimplay-sk" />
                                    <span className="vimplay-skeleton-name vimplay-sk" />
                                    <span className="vimplay-skeleton-category vimplay-sk" />
                                </div>
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
}

const VimplayGames = () => {
    usePlayerChrome();
    const { user, userIpData } = useUser();
    const canAccess = canAccessVimplayGames(user);
    const [games, setGames] = useState<VimplayGame[]>([]);
    const [query, setQuery] = useState("");
    const [categoryId, setCategoryId] = useState<"all" | VimplayLobbyCategoryId>("all");
    const [bonuses, setBonuses] = useState<VimplayPlayerBonus[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [launchingGameId, setLaunchingGameId] = useState<number | null>(null);
    const [selectedGameId, setSelectedGameId] = useState<number | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const launchingRef = useRef(false);

    useEffect(() => {
        if (!canAccess || !user?.userName) return;
        logUserActivity(user.userName, "View Games", userIpData);
    }, [canAccess, user?.userName, userIpData]);

    useEffect(() => {
        if (!canAccess) return;
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            setErrorMessage("");
            try {
                const [result, playerBonuses] = await Promise.all([
                    getVimplayGames(),
                    user?.id ?
                        getPlayerVimplayBonuses(user.id).catch(() => []) :
                        Promise.resolve([]),
                ]);
                if (!cancelled) {
                    setGames(result);
                    setBonuses(playerBonuses);
                }
            } catch (error) {
                console.error("Error loading Vimplay games:", error);
                if (!cancelled) {
                    setGames([]);
                    setErrorMessage("Unable to load games right now. Please try again.");
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [canAccess, user?.id]);

    const visibleGames = useMemo(() => {
        const term = query.trim().toLowerCase();
        return games.filter((game) => {
            if (categoryId !== "all" && vimplayLobbyCategory(game).id !== categoryId) return false;
            if (term && !(game.name ?? "").toLowerCase().includes(term)) return false;
            return true;
        });
    }, [games, query, categoryId]);

    const sections = useMemo(() => {
        return VIMPLAY_LOBBY_CATEGORIES
            .map((category) => ({
                ...category,
                games: visibleGames
                    .filter((game) => vimplayLobbyCategory(game).id === category.id)
                    .sort((a, b) => compareVimplayLobbyGames(category.id, a, b)),
            }))
            .filter((section) => section.games.length > 0);
    }, [visibleGames]);

    const handleLaunch = async (game: VimplayGame, gameTab: Window | null) => {
        if (!canAccess || game.id == null || launchingRef.current) {
            gameTab?.close();
            return;
        }
        if (!gameTab) {
            setErrorMessage("Allow pop-ups to open this game.");
            return;
        }

        launchingRef.current = true;
        setSelectedGameId(game.id);
        setLaunchingGameId(game.id);
        setErrorMessage("");
        try {
            const gameUrl = await getVimplayLaunchUrl(game.id);
            if (gameTab.closed) return;
            gameTab.location.replace(gameUrl);
            gameTab.opener = null;
        } catch (error) {
            console.error("Error launching Vimplay game:", error);
            if (!gameTab.closed) gameTab.close();
            setErrorMessage("Unable to launch this game right now. Please try again.");
        } finally {
            launchingRef.current = false;
            setLaunchingGameId(null);
        }
    };

    const countLabel = `${visibleGames.length} ${visibleGames.length === 1 ? "game" : "games"}`;

    if (!canAccess) {
        return <Navigate to="/home" replace />;
    }

    return (
        <div className="player-layout">
            <Navbar />
            <main className="custom-container grow vimplay-page">
                <div className="vimplay-heading">
                    <h1 className="vimplay-title teko">Casino</h1>
                    {!isLoading && games.length > 0 && (
                        <>
                            <span className="vimplay-heading-sep" aria-hidden="true">|</span>
                            <span className="vimplay-count">{countLabel}</span>
                        </>
                    )}
                </div>
                {!isLoading && games.length > 0 && (
                    <>
                        <label className="vimplay-search">
                            <FiSearch className="vimplay-search-icon" aria-hidden="true" />
                            <input
                                type="search"
                                className="vimplay-search-input"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Search games..."
                                aria-label="Search games"
                                autoComplete="off"
                            />
                        </label>
                        <div className="vimplay-filters" role="tablist" aria-label="Game categories">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={categoryId === "all"}
                                className={`vimplay-filter${categoryId === "all" ? " is-active" : ""}`}
                                onClick={() => setCategoryId("all")}
                            >
                                All Games
                            </button>
                            {VIMPLAY_LOBBY_CATEGORIES.map((category) => (
                                <button
                                    key={category.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={categoryId === category.id}
                                    className={`vimplay-filter${categoryId === category.id ? " is-active" : ""}`}
                                    onClick={() => setCategoryId(category.id)}
                                >
                                    {category.label}
                                </button>
                            ))}
                        </div>
                    </>
                )}
                {isLoading ? (
                    <LobbySkeleton />
                ) : games.length === 0 ? (
                    <div className="vimplay-empty teko">There are currently no games available.</div>
                ) : sections.length === 0 ? (
                    <div className="vimplay-empty teko">No games match your search.</div>
                ) : categoryId === "all" ? (
                    <div className="vimplay-sections">
                        {sections.map((section) => (
                            <CategoryRail
                                key={section.id}
                                title={section.label}
                                games={section.games}
                                bonuses={bonuses}
                                launchingGameId={launchingGameId}
                                selectedGameId={selectedGameId}
                                onLaunch={handleLaunch}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="vimplay-sections">
                        {sections.map((section) => (
                            <section key={section.id} className="vimplay-section" aria-label={section.label}>
                                <h2 className="vimplay-section-title">{section.label}</h2>
                                <div className="vimplay-grid">
                                    {section.games.map((game, index) => (
                                        <GameCard
                                            key={`${game.id ?? game.name ?? "game"}-${index}`}
                                            game={game}
                                            categoryLabel={section.label}
                                            isLive={vimplayGameIsLive(game)}
                                            bonus={bonuses.find((bonus) => bonus.gameId === game.id)}
                                            isLaunching={launchingGameId === game.id}
                                            isSelected={selectedGameId === game.id}
                                            onLaunch={handleLaunch}
                                        />
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                )}
            </main>
            <Footer />
            {errorMessage && (
                <AlertError message={errorMessage} onClose={() => setErrorMessage("")} />
            )}
        </div>
    );
};

export default VimplayGames;
